import { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from '../../../reusable/Toast/toastStore';
import { t } from '../../../i18n/i18n';
import './ShareCertificateDialog.scss';

/**
 * Off the app's own origin, so a preview deploy hands out its own viewer
 * rather than pointing every tester at production.
 */
export const shareCertificateLink = (id) =>
  id ? `${window.location.origin}/share-certificates/${id}` : '';

/** Each network takes the link in its own query param; none of them post on
 *  the trader's behalf — every one is a link that opens the composer. */
const SOCIALS = [
  {
    key: 'facebook',
    label: 'Facebook',
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    path: 'M13.4 21v-7.6h2.6l.4-3h-3V8.5c0-.86.24-1.45 1.48-1.45h1.58V4.38A21 21 0 0 0 14.15 4.3c-2.28 0-3.85 1.4-3.85 3.95v2.2H7.7v3h2.6V21z',
  },
  {
    key: 'x',
    label: 'X',
    href: (url, text) => `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
    path: 'M17.2 3.5h2.9l-6.3 7.2 7.4 9.8h-5.8l-4.5-5.9-5.2 5.9H2.8l6.7-7.7L2.4 3.5h5.9l4.1 5.4zm-1 14.7h1.6L7.9 5.2H6.2z',
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    href: (url, text) => `https://wa.me/?text=${text}%20${url}`,
    path: 'M12 3a8.9 8.9 0 0 0-7.6 13.6L3.2 21l4.5-1.2A8.9 8.9 0 1 0 12 3zm5.2 12.6c-.22.62-1.3 1.2-1.8 1.24-.46.05-1.05.07-1.7-.1a15.4 15.4 0 0 1-1.54-.58 12 12 0 0 1-4.6-4.08c-.34-.47-.76-1.24-.76-2.06 0-.82.43-1.22.58-1.39a.6.6 0 0 1 .45-.2h.32c.1 0 .24-.04.38.3l.52 1.3c.04.1.07.2 0 .32l-.2.3-.3.33c-.1.1-.2.21-.09.42.12.2.53.87 1.13 1.4.78.7 1.44.92 1.64 1.02.2.1.32.09.44-.05l.63-.73c.15-.17.28-.13.46-.07l1.3.61c.2.1.32.15.37.23.04.08.04.47-.18 1.09z',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    href: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    path: 'M4.6 9h2.9v11H4.6zM6 4.2a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5zM9.6 9h2.8v1.5a3.1 3.1 0 0 1 2.8-1.6c3 0 3.5 1.9 3.5 4.5V20h-2.9v-5c0-1.2 0-2.7-1.65-2.7-1.66 0-1.91 1.27-1.91 2.6V20H9.6z',
  },
  {
    key: 'telegram',
    label: 'Telegram',
    href: (url, text) => `https://t.me/share/url?url=${url}&text=${text}`,
    path: 'M21.2 4.3 2.9 11.2c-.9.34-.9.87-.15 1.1l4.6 1.44 1.75 5.3c.2.56.36.55.7.55.34 0 .5-.16.7-.35l2.2-2.14 4.55 3.36c.84.46 1.44.22 1.65-.77l2.98-14.03c.3-1.22-.47-1.77-1.28-1.4zM8.5 13.4l9.9-6.25-7.67 6.94-.3 3.2z',
  },
  {
    key: 'reddit',
    label: 'Reddit',
    href: (url, text) => `https://www.reddit.com/submit?url=${url}&title=${text}`,
    path: 'M22 12.1a2.06 2.06 0 0 0-3.5-1.45 10.1 10.1 0 0 0-5.35-1.7l.9-4.26 2.96.63a1.75 1.75 0 1 0 .2-1.45l-3.65-.78a.62.62 0 0 0-.74.48l-1.1 5.36a10.1 10.1 0 0 0-5.43 1.7A2.06 2.06 0 1 0 3.9 14a4.1 4.1 0 0 0-.05.63c0 3.2 3.65 5.8 8.15 5.8s8.15-2.6 8.15-5.8c0-.21-.02-.42-.05-.62A2.06 2.06 0 0 0 22 12.1zM7.5 13.6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm8.4 4a5.7 5.7 0 0 1-3.9 1.2 5.7 5.7 0 0 1-3.9-1.2.55.55 0 1 1 .76-.8 4.7 4.7 0 0 0 3.14.9 4.7 4.7 0 0 0 3.14-.9.55.55 0 1 1 .76.8zm-.4-2.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z',
  },
  {
    key: 'email',
    label: 'Email',
    href: (url, text) => `mailto:?subject=${text}&body=${url}`,
    path: 'M3.5 5h17c.83 0 1.5.67 1.5 1.5v11c0 .83-.67 1.5-1.5 1.5h-17A1.5 1.5 0 0 1 2 17.5v-11C2 5.67 2.67 5 3.5 5zm8.5 7.9 8-5.2V6.6l-8 5.2-8-5.2v1.1z',
  },
];

/** The scheme is noise in a link the trader is reading, not copying. */
const displayLink = (link) => link.replace(/^https?:\/\//, '');

/**
 * "Share" on a certificate.
 *
 * Sharing is server state — the link only resolves once the row's `public`
 * flag is on — and publishing a certificate is the trader's call, so opening
 * this shows the off state and "Generate link" is what issues the flag.
 */
export default function ShareCertificateDialog({ open, item, busy, onSetPublic, onClose }) {
  const isPublic = Boolean(item?.public);
  const link = isPublic ? shareCertificateLink(item?.id) : '';
  const text = encodeURIComponent(item?.title ? t(item.title) : t('My Alpha Capital certificate'));

  // The toast confirms the copy, but it lands in the corner while the pointer
  // is still on the button — so the button says so too, and puts itself back.
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const onCopy = (_value, ok) => {
    if (!ok) {
      toast.error(t('Could not copy the link'), t('Select the link and copy it manually.'));
      return;
    }

    toast.success(t('Link copied'), t('Paste it anywhere you want to share this certificate.'));
    setCopied(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 2200);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={460}
      zIndex={1100}
      destroyOnHidden
      className="cert-share"
      rootClassName="cert-share-root"
      aria-label={t('Share certificate')}
    >
      <div className="cert-share__body">
        <header className="cert-share__head">
          <h2 className="cert-share__title">{t('Share certificate')}</h2>
          {item?.title && <p className="cert-share__subtitle">{t(item.title)}</p>}
        </header>

        {isPublic ? (
          <>
            <div className="cert-share__status">
              <span className="cert-share__dot" />
              <div className="cert-share__status-text">
                <strong>{t('Link is live')}</strong>
                <span>{t('Anyone with the link can view this certificate.')}</span>
              </div>
            </div>

            <div className="cert-share__link">
              <LinkIcon />
              <span className="cert-share__url" title={link}>
                {displayLink(link)}
              </span>

              <CopyToClipboard text={link} onCopy={onCopy}>
                <button
                  type="button"
                  className={copied ? 'cert-share__copy is-copied' : 'cert-share__copy'}
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  {copied ? t('Copied') : t('Copy Link')}
                </button>
              </CopyToClipboard>
            </div>

            <div className="cert-share__socials">
              <span className="cert-share__socials-label">{t('Share via')}</span>

              <div className="cert-share__socials-row">
                {SOCIALS.map((social) => (
                  <a
                    key={social.key}
                    className={`cert-share__social is-${social.key}`}
                    href={social.href(encodeURIComponent(link), text)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={t('Share on {{network}}', { network: t(social.label) })}
                    title={t(social.label)}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d={social.path} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            <footer className="cert-share__footer">
              <span className="cert-share__footer-text">
                {t('Disabling breaks the link for everyone you have sent it to.')}
              </span>

              <button
                type="button"
                className="cert-share__disable"
                onClick={() => onSetPublic(item, false)}
                disabled={busy}
              >
                {busy && <Spinner />}
                {busy ? t('Disabling') : t('Disable link')}
              </button>
            </footer>
          </>
        ) : (
          <>
            <div className="cert-share__status is-off">
              <span className="cert-share__dot" />
              <div className="cert-share__status-text">
                <strong>{t('This certificate is private')}</strong>
                <span>{t('Generate a link to let anyone view it without signing in.')}</span>
              </div>
            </div>

            <button
              type="button"
              className="cert-share__generate"
              onClick={() => onSetPublic(item, true)}
              disabled={busy}
            >
              {busy && <Spinner />}
              {busy ? t('Generating link') : t('Generate link')}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

function Spinner() {
  return <span className="cert-share__spinner" aria-hidden="true" />;
}

function LinkIcon() {
  return (
    <svg
      className="cert-share__link-icon"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
    >
      <path d="M5.9 8.1a2.6 2.6 0 0 0 3.9.3l1.6-1.6a2.6 2.6 0 0 0-3.7-3.7l-.9.9" />
      <path d="M8.1 5.9a2.6 2.6 0 0 0-3.9-.3L2.6 7.2a2.6 2.6 0 0 0 3.7 3.7l.9-.9" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
      <rect x="5" y="5" width="6.6" height="6.6" rx="1.6" />
      <path d="M9 3.6a1.6 1.6 0 0 0-1.6-1.2H4a1.6 1.6 0 0 0-1.6 1.6v3.4A1.6 1.6 0 0 0 3.6 9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="m3.2 7.3 2.6 2.6 5-5.6" />
    </svg>
  );
}
